// ============================================
// 📱 APP: NVA (React App)
// 📄 FILE: src/lib/locale.ts
// 🔧 PURPOSE: Le lingue dell'app, in un posto solo.
//
//             L'app parla le stesse otto lingue del check-in online. Prima
//             ne parlava due, e in quarantasette punti del codice c'era
//             scritto "se è italiano usa it-IT, altrimenti en-US": con la
//             terza lingua ogni data e ogni prezzo sarebbero usciti in
//             inglese. Da qui passano tutti: chi ha bisogno di un locale per
//             `Intl` chiede `intlLocale(i18n.language)` e basta.
//
//             Qui vive anche la regola della lingua automatica: al primo
//             accesso l'app si imposta nella lingua scelta al check-in
//             online, ma se l'ospite ne sceglie un'altra a mano vince lui.
// ============================================

import i18n from '../i18n';

export const APP_LANGUAGES = [
  { code: 'it', label: 'Italiano' },
  { code: 'en', label: 'English' },
  { code: 'de', label: 'Deutsch' },
  { code: 'es', label: 'Español' },
  { code: 'fr', label: 'Français' },
  { code: 'pl', label: 'Polski' },
  { code: 'pt', label: 'Português' },
  { code: 'zh', label: '中文' },
] as const;

export type AppLanguage = typeof APP_LANGUAGES[number]['code'];

export const APP_LANGUAGE_CODES: string[] = APP_LANGUAGES.map((l) => l.code);

const INTL_LOCALES: Record<AppLanguage, string> = {
  it: 'it-IT',
  en: 'en-GB',
  de: 'de-DE',
  es: 'es-ES',
  fr: 'fr-FR',
  pl: 'pl-PL',
  pt: 'pt-PT',
  zh: 'zh-CN',
};

/** La lingua dell'app corrispondente a un codice qualsiasi ("de-AT" → "de"); inglese se sconosciuta. */
export function appLanguageOf(lang?: string | null): AppLanguage {
  const base = String(lang || '').toLowerCase().split('-')[0];
  return (APP_LANGUAGE_CODES.indexOf(base) >= 0 ? base : 'en') as AppLanguage;
}

/** Il locale per `Intl.DateTimeFormat` / `toLocaleString` della lingua corrente. */
export function intlLocale(lang?: string | null): string {
  return INTL_LOCALES[appLanguageOf(lang)];
}

/** Etichetta leggibile di una lingua ("Deutsch"). */
export function languageLabel(lang?: string | null): string {
  const code = appLanguageOf(lang);
  return APP_LANGUAGES.filter((l) => l.code === code)[0]?.label || code.toUpperCase();
}

/**
 * Quando l'ospite sceglie la lingua a mano lo si ricorda: da quel momento la
 * lingua del check-in non la sovrascrive più. Senza questo, chi vuole l'app
 * in inglese pur avendo fatto il check-in in tedesco la rivedrebbe in
 * tedesco a ogni nuovo accesso.
 */
const MANUAL_LANGUAGE_KEY = 'nva-language-manual';

export function setLanguageManually(lang: string): void {
  const code = appLanguageOf(lang);
  try { localStorage.setItem(MANUAL_LANGUAGE_KEY, code); } catch { /* storage non disponibile */ }
  i18n.changeLanguage(code);
}

/** La lingua scelta a mano dall'ospite, o null se non l'ha mai scelta. */
export function manualLanguage(): AppLanguage | null {
  try {
    const saved = localStorage.getItem(MANUAL_LANGUAGE_KEY);
    return saved ? appLanguageOf(saved) : null;
  } catch {
    return null; // storage non disponibile
  }
}

/**
 * La lingua del check-in online, applicata al login (anche automatico da
 * link o notifica). Ritorna true se la lingua è cambiata: chi chiama può
 * dirlo all'ospite con un avviso.
 */
export function applyGuestLanguage(booking?: { language?: string | null } | null): boolean {
  const wanted = booking?.language;
  if (!wanted) return false;
  const code = String(wanted).toLowerCase().split('-')[0];
  if (APP_LANGUAGE_CODES.indexOf(code) < 0) return false;
  try {
    if (localStorage.getItem(MANUAL_LANGUAGE_KEY)) return false;
  } catch { /* storage non disponibile: si applica comunque */ }
  if (appLanguageOf(i18n.language) === code) return false;
  i18n.changeLanguage(code);
  return true;
}
